import React, { useState } from 'react';
import moment from 'moment';
import { Global } from '@emotion/core';
import { ThreeDotMenu } from 'components/EpisodeEditorPublish/components/InteractiveQuestion/ThreeDotMenu';
import { ReplyAvatar } from 'components/EpisodeEditorPublish/components/InteractiveQuestion/ReplyAvatar';
import { useFetchQuestionAndResponses } from 'hooks/useInteractivityPolls';
import { SpotifyLogo } from 'components/svgs/SpotifyLogo';
import { Modal } from 'shared/Modal';
import { InteractiveQuestionCreateForm } from '../InteractiveQuestionCreateForm';
import {
  PollModalTitle,
  InteractivePollContainer,
  Title,
  PollBanner,
  CopyContainer,
  FormContainer,
  OpenModalButton,
  PollQuestionDisplayCopy,
  StatusText,
  Optional,
  TopBarContainer,
  BootstrapOverrideStyles,
} from '../InteractivePoll/styles';
import { ReplyContainer, ReplyText, ReplyName, ReplyTime } from './styles';
import { ReplyActions } from './ReplyActions';

export function InteractiveQuestion({ episodeId }: { episodeId: string }) {
  const { data, status } = useFetchQuestionAndResponses(episodeId);
  const [isCreateQuestionModalOpen, setIsCreateQuestionModalOpen] = useState(
    false
  );
  const [isRepliesModalOpen, setIsRepliesModalOpen] = useState(false);
  if (!data || status === 'loading') return null;

  const {
    question: { text = '', openingDate, closingDate, questionId } = {},
    featuredReplies = [],
    allReplies = [],
  } = data;

  const initialValues = {
    text,
  };

  return (
    <InteractivePollContainer>
      <Global styles={BootstrapOverrideStyles} />
      <Title>Q&amp;A</Title> <Optional>(optional)</Optional>
      <PollBanner>
        <CopyContainer>
          <TopBarContainer>
            <SpotifyLogo width={63} />
            {questionId && (
              <ThreeDotMenu
                episodeId={episodeId}
                questionId={questionId}
                onClickSeeReplies={() => setIsRepliesModalOpen(true)}
              />
            )}
          </TopBarContainer>
          <PollQuestionDisplayCopy>
            {text ||
              `Engage with listeners directly from your episode with a question`}
          </PollQuestionDisplayCopy>
          {openingDate && (
            <StatusText>{moment.utc(openingDate).format('M/D/YY')}</StatusText>
          )}
        </CopyContainer>
        {questionId ? (
          <OpenModalButton
            kind="button"
            type="button"
            onClick={() => setIsRepliesModalOpen(!isRepliesModalOpen)}
            color="purple"
          >
            Pin replies
          </OpenModalButton>
        ) : (
          <OpenModalButton
            kind="button"
            type="button"
            onClick={() =>
              setIsCreateQuestionModalOpen(!isCreateQuestionModalOpen)
            }
            color="purple"
          >
            Ask a question
          </OpenModalButton>
        )}
      </PollBanner>
      {isCreateQuestionModalOpen && (
        <Modal
          isShowing={true}
          isShowingCloseButton
          onClickOutside={() => setIsCreateQuestionModalOpen(false)}
          onClickClose={() => setIsCreateQuestionModalOpen(false)}
          renderContent={() => (
            <FormContainer>
              <InteractiveQuestionCreateForm
                onSuccess={() => setIsCreateQuestionModalOpen(false)}
                initialValues={initialValues}
                episodeId={episodeId}
              />
            </FormContainer>
          )}
        />
      )}
      {isRepliesModalOpen && (
        <Modal
          isShowing={true}
          isShowingCloseButton
          onClickOutside={() => setIsRepliesModalOpen(false)}
          onClickClose={() => setIsRepliesModalOpen(false)}
          renderContent={() => (
            <FormContainer>
              <PollModalTitle>Replies</PollModalTitle>
              <div>
                {allReplies.length === 0 && <p>No replies yet.</p>}
                {questionId &&
                  allReplies.map(
                    (
                      {
                        text,
                        userInfo: { displayName, profileImageUri },
                        repliedAtEpochTime,
                        spotifyUserId,
                        isFeatured,
                        id,
                      },
                      index: number
                    ) => (
                      <ReplyContainer>
                        <ReplyAvatar
                          isPinned={isFeatured}
                          imageUrl={profileImageUri}
                        />
                        <ReplyText>{text}</ReplyText>
                        <ReplyName>{displayName}</ReplyName>
                        <ReplyTime>
                          {moment
                            .utc(repliedAtEpochTime * 1000)
                            .format('M/D/YY')}
                        </ReplyTime>
                        <ReplyActions
                          episodeId={episodeId}
                          questionId={questionId}
                          replyId={id}
                          spotifyUserId={spotifyUserId}
                          isPinned={isFeatured}
                        />
                      </ReplyContainer>
                    )
                  )}
              </div>
            </FormContainer>
          )}
        />
      )}
    </InteractivePollContainer>
  );
}
